import { Vector2 } from 'three';
import { Rectangle } from '@thive/fddm-types';

import { Area, Template } from '@/modules/floorplanDesign/types';

import {
  getAreaRectangles,
  getBoundingBoxSize,
  getCenter,
  getPositionDelta,
  normalizeAngle,
} from './helpers';
import { DimensionItem, getItemRectangles, offsetItem, rotateItemAround } from './items';
import { DimensionField, Geometry } from './types';

type Center = ReturnType<typeof getCenter>;

type SelectionPose = { center: Center; angle: number; rectangles: Rectangle[] };

/**
 * @description The pose of a selection with templates: the centre of everything's bounding box
 * (templates through their areas) and, as angle, the first item's, else the first template's.
 * A template on its own uses its own centre (its generation params) instead.
 * The same pose is shown and edited, so a shown value typed back changes nothing.
 */
const getSelectionPose = (
  items: DimensionItem[],
  templates: Template[],
  templateAreas: Area[],
): SelectionPose | null => {
  const itemRectangles = items.flatMap(getItemRectangles);
  const rectangles = [...itemRectangles, ...templateAreas.flatMap(getAreaRectangles)];
  if (rectangles.length === 0) return null;

  const angle = itemRectangles[0]?.angle ?? templates[0]?.params.angle ?? 0;
  const [onlyTemplate] = templates;
  const center =
    items.length === 0 && templates.length === 1 && onlyTemplate
      ? { centerX: onlyTemplate.params.centerX, centerY: onlyTemplate.params.centerY }
      : getCenter(rectangles);

  return { center, angle, rectangles };
};

/** @description Read-only size: the bounding box, like for any group */
export const getTemplatesGeometry = (
  items: DimensionItem[],
  templates: Template[],
  templateAreas: Area[],
): Geometry | null => {
  const pose = getSelectionPose(items, templates, templateAreas);
  if (!pose) return null;

  const { length, width } = getBoundingBoxSize(pose.rectangles);
  return {
    centerX: pose.center.centerX,
    centerY: pose.center.centerY,
    length,
    width,
    angle: pose.angle,
    canResize: false,
    canRotate: true,
  };
};

/** @description A template moves through its generation params: turned around `center`, then offset */
const moveTemplate = <T extends Template>(
  template: T,
  offset: Vector2,
  center: Center,
  angleDelta: number,
): T => {
  const position = new Vector2(template.params.centerX, template.params.centerY)
    .rotateAround(new Vector2(center.centerX, center.centerY), angleDelta)
    .add(offset);

  return {
    ...template,
    params: {
      ...template.params,
      centerX: position.x,
      centerY: position.y,
      angle: template.params.angle + angleDelta,
    },
  };
};

/** @description Moves a template by the offset, through its generation params */
export const offsetTemplate = <T extends Template>(template: T, offset: Vector2): T =>
  moveTemplate(template, offset, { centerX: 0, centerY: 0 }, 0);

/** @description Turns a template around `center` by `angleDelta`, through its generation params */
export const rotateTemplateAround = <T extends Template>(
  template: T,
  center: Center,
  angleDelta: number,
): T => moveTemplate(template, new Vector2(), center, angleDelta);

/**
 * @description Moves (X/Y) or rotates (angle, around the selection centre) the items and templates
 * together. Length and width are read-only. Returns null when nothing changes.
 */
export const updateTemplatesField = (
  items: DimensionItem[],
  templates: Template[],
  templateAreas: Area[],
  field: DimensionField,
  value: number,
): { items: DimensionItem[]; templates: Template[] } | null => {
  const pose = getSelectionPose(items, templates, templateAreas);
  if (!pose) return null;

  if (field === 'centerX' || field === 'centerY') {
    const delta = getPositionDelta(field, value, pose.center);
    if (delta === 0) return null;

    const offset = new Vector2(field === 'centerX' ? delta : 0, field === 'centerY' ? delta : 0);
    return {
      items: items.map((item) => offsetItem(item, offset)),
      templates: templates.map((template) => moveTemplate(template, offset, pose.center, 0)),
    };
  }

  if (field === 'angle') {
    const angleDelta = normalizeAngle(value - pose.angle);
    if (angleDelta === 0) return null;

    return {
      items: items.map((item) => rotateItemAround(item, pose.center, angleDelta)),
      templates: templates.map((template) =>
        moveTemplate(template, new Vector2(), pose.center, angleDelta),
      ),
    };
  }

  return null;
};
